import React, { useEffect, useState } from 'react';
import { Paper, Title, Text, Group, Badge, Stack } from '@mantine/core';
import { IconSchool, IconUsers } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveSeason, isSeasonPastForUser } from '../../config/seasons';
import CollapsibleRoster from './CollapsibleRoster';

/**
 * SchoolPastSeasonsSection
 *
 * The school counterpart to PastCompetitionsSection. SchoolAccountSection only
 * ever shows the active season, so without this a school's 2026 roster would
 * disappear from the teacher's profile the moment 2027 becomes active.
 *
 * A season qualifies as past when its competition day has gone AND it is not
 * the active season — so through the 2026 tail, when 2026 is both past and
 * still active, the roster stays in the Studio Portal card above rather than
 * appearing twice.
 *
 * Renders nothing while loading or with no history, so a first-year school
 * never sees an empty box.
 */
const SchoolPastSeasonsSection = () => {
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) { setLoaded(true); return undefined; }

    (async () => {
      try {
        const { data: membership } = await supabase
          .from('school_members')
          .select('schools ( id, name )')
          .eq('user_id', user.id)
          .maybeSingle();

        const school = membership?.schools;
        if (!school?.id) return;

        const { data: seasonRows } = await supabase
          .from('school_seasons')
          .select('id, competition_year')
          .eq('school_id', school.id)
          .order('competition_year', { ascending: false });

        const activeYear = getActiveSeason().year;
        const past = (seasonRows || []).filter(s =>
          s.competition_year !== activeYear
          && isSeasonPastForUser(s.competition_year, user.email)
        );
        if (past.length === 0) return;

        // One roster query for all past seasons, then grouped in memory —
        // a school has a handful of seasons, not a page of them.
        const { data: rows } = await supabase
          .from('registrations')
          .select(`
            id, school_season_id, status, payment_status, parent_consent_status,
            dancer_information ( first_name, last_name, date_of_birth ),
            variations ( id, variation_number, variation_name, music_file_url ),
            judging_reports ( id, variation_id, rank, published_at )
          `)
          .in('school_season_id', past.map(s => s.id))
          .eq('registration_type', 'school')
          .order('created_at', { ascending: true });

        const bySeason = (rows || []).reduce((acc, r) => {
          (acc[r.school_season_id] ||= []).push(r);
          return acc;
        }, {});

        if (!cancelled) {
          setSeasons(past.map(s => ({
            ...s,
            schoolName: school.name,
            students: bySeason[s.id] || [],
          })));
        }
      } catch (err) {
        console.error('SchoolPastSeasonsSection load error:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, user?.email]);

  if (!loaded || seasons.length === 0) return null;

  return (
    <div>
      <Title order={3} size="h4" mb="xs" style={{ color: 'var(--charcoal)', textAlign: 'left' }}>
        Past Seasons
      </Title>
      <Text size="sm" c="dimmed" mb="md" style={{ textAlign: 'left' }}>
        Your studio's record from previous seasons. Judging reports appear here once results are released.
      </Text>

      <Stack gap="md">
        {seasons.map((season) => (
          <Paper
            key={season.id}
            radius="md"
            p="xl"
            withBorder
            style={{ borderTop: '4px solid var(--primary-gold)' }}
          >
            <Group justify="space-between">
              <Group>
                <IconSchool size={22} style={{ color: 'var(--primary-gold)' }} />
                <Title order={4} size="h5" style={{ color: 'var(--charcoal)' }}>
                  Prix du Canada {season.competition_year}
                </Title>
              </Group>
              <Badge color="gold" variant="light" leftSection={<IconUsers size={12} />}>
                {season.students.length} student{season.students.length !== 1 ? 's' : ''}
              </Badge>
            </Group>

            {/* The season's own year, not today's — a 2026 dancer must stay in
                the division they actually competed in. */}
            <CollapsibleRoster students={season.students} year={season.competition_year} />
          </Paper>
        ))}
      </Stack>
    </div>
  );
};

export default SchoolPastSeasonsSection;
